// src/hooks/useTownships.ts
import {useState, useCallback, useMemo} from "react";
import {
  useGetTownshipsQuery,
  useGetTownshipsByRegionQuery,
  useGetTownshipByIdQuery,
  useCreateTownshipMutation,
  useUpdateTownshipMutation,
  useDeleteTownshipMutation,
  useBulkCreateTownshipsMutation,
  useGetTownshipStatisticsQuery,
} from "../store/api/townshipsApi";
import {useToast} from "./common/useToast";
import {useConfirm} from "./common/useConfirm";
import type {
  TownshipFilterDto,
  CreateTownshipDto,
  UpdateTownshipDto,
} from "../types/township";

export const useTownships = (filters: TownshipFilterDto = {}) => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();

  // Query hooks
  const {
    data: townshipsResponse,
    isLoading,
    isFetching,
    error: fetchError,
    refetch,
  } = useGetTownshipsQuery(filters);

  const {data: statsData, isLoading: statsLoading} =
    useGetTownshipStatisticsQuery();

  // Mutation hooks
  const [createTownship, {isLoading: isCreating}] = useCreateTownshipMutation();
  const [updateTownship, {isLoading: isUpdating}] = useUpdateTownshipMutation();
  const [deleteTownship, {isLoading: isDeleting}] = useDeleteTownshipMutation();
  const [bulkCreateTownships, {isLoading: isBulkCreating}] =
    useBulkCreateTownshipsMutation();

  // Memoized values
  const townships = useMemo(
    () => townshipsResponse?.data || [],
    [townshipsResponse]
  );
  const pagination = useMemo(
    () => townshipsResponse?.meta,
    [townshipsResponse]
  );

  // Handlers
  // src/hooks/useTownships.ts - Improved handleCreate function
  const handleCreate = useCallback(
    async (data: CreateTownshipDto) => {
      try {
        await createTownship(data).unwrap();
        success("Success", "Township created successfully");
        return true;
      } catch (err: any) {
        console.error("Township creation error:", err);

        // Extract meaningful error message from backend response
        let errorMessage = "Failed to create township";

        if (err?.data?.message) {
          // Backend provided specific error message
          errorMessage = err.data.message;
        } else if (err?.data?.errors && Array.isArray(err.data.errors)) {
          // Backend provided validation errors array
          const validationErrors = err.data.errors
            .map((e: any) => e.message || e.field)
            .join(", ");
          errorMessage = `Validation failed: ${validationErrors}`;
        } else if (err?.message) {
          // RTK Query error message
          errorMessage = err.message;
        } else if (err?.status === 409) {
          // Conflict error (duplicate slug)
          errorMessage = "A township with this name or URL slug already exists";
        } else if (err?.status === 400) {
          // Bad request
          errorMessage = "Invalid township data provided";
        } else if (err?.status === 422) {
          // Unprocessable entity (validation error)
          errorMessage = "Please check your input data and try again";
        }

        error("Error", errorMessage);

        // Re-throw the error so components can handle it further if needed
        throw err;
      }
    },
    [createTownship, success, error]
  );

  // Similarly, improve handleUpdate
  const handleUpdate = useCallback(
    async (id: number, data: UpdateTownshipDto) => {
      try {
        await updateTownship({id, data}).unwrap();
        success("Success", "Township updated successfully");
        return true;
      } catch (err: any) {
        console.error("Township update error:", err);

        let errorMessage = "Failed to update township";

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.errors && Array.isArray(err.data.errors)) {
          const validationErrors = err.data.errors
            .map((e: any) => e.message || e.field)
            .join(", ");
          errorMessage = `Validation failed: ${validationErrors}`;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (err?.status === 409) {
          errorMessage = "A township with this name or URL slug already exists";
        } else if (err?.status === 404) {
          errorMessage = "Township not found";
        } else if (err?.status === 400) {
          errorMessage = "Invalid township data provided";
        } else if (err?.status === 422) {
          errorMessage = "Please check your input data and try again";
        }

        error("Error", errorMessage);
        throw err;
      }
    },
    [updateTownship, success, error]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const confirmed = await confirm({
        title: "Delete Township",
        message:
          "Are you sure you want to delete this township? All associated properties will be affected.",
        variant: "danger",
        confirmText: "Delete",
      });

      if (!confirmed) return false;

      try {
        await deleteTownship(id).unwrap();
        success("Success", "Township deleted successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to delete township");
        return false;
      }
    },
    [deleteTownship, confirm, success, error]
  );

  const handleBulkCreate = useCallback(
    async (townshipsData: CreateTownshipDto[]) => {
      try {
        await bulkCreateTownships({townships: townshipsData}).unwrap();
        success(
          "Success",
          `${townshipsData.length} townships created successfully`
        );
        return true;
      } catch (err) {
        error("Error", "Failed to create townships");
        return false;
      }
    },
    [bulkCreateTownships, success, error]
  );

  return {
    // Data
    townships,
    pagination,
    stats: statsData,

    // Loading states
    isLoading: isLoading || isFetching,
    statsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isBulkCreating,

    // Error
    error: fetchError,

    // Actions
    refetch,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleBulkCreate,
  };
};

// Hook for townships by region
export const useTownshipsByRegion = (regionId: number | undefined) => {
  const {
    data: townships,
    isLoading,
    error,
    refetch,
  } = useGetTownshipsByRegionQuery(regionId!, {
    skip: !regionId,
  });

  return {
    townships: townships || [],
    isLoading,
    error,
    refetch,
  };
};

// Hook for single township
export const useTownship = (id: number | undefined) => {
  const {
    data: township,
    isLoading,
    error,
    refetch,
  } = useGetTownshipByIdQuery(id!, {
    skip: !id,
  });

  return {
    township,
    isLoading,
    error,
    refetch,
  };
};
